import { ReportData, ReportInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/report_pb";
import { Box, Collapse, Divider, IconButton, SvgIcon, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { formatISO } from "date-fns";
import { convertTimestampObjectToDate } from "../../common/utils/formatter";
import { getUserFullNameFromObject } from "../settings/ProjectUsers";
import CdsLogo from "../../assets/logo300dark.png";
import { ColumnSortFilterConfig } from "../core/data/ListData";
import { ListProjectReports } from "gc-web-proto/galaxycompletepb/apipb/report_api_pb";
import { SortState } from "../../common/table/DataTable";
import { FilterParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";

export interface ReportProps {
    reportData: ReportData.AsObject;
    reportInfo: ReportInfo.AsObject;
}

interface CollapsibleHeaderProps {
    title: string;
    children: React.ReactNode;
    indent?: number;
}

// ======================
// CollapsibleHeader
// ======================
export const CollapsibleHeader: React.FC<CollapsibleHeaderProps> = (p) => {
    const { title, children } = p;
    const theme = useTheme();

    const [expanded, setExpanded] = useState(true);
    const icon = expanded ? <IoIosArrowUp /> : <IoIosArrowDown />;
    const { t } = useTranslation("reports");

    return (
        <Box>
            <Box
                sx={{
                    backgroundColor: theme.palette.cirrus.main,
                    color: "white",
                    "@media print": {
                        color: "black",
                    },
                    "@media screen": {
                        padding: 2,
                        marginRight: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        "@media print": {
                            display: "block",
                        },
                        "@media screen": {
                            display: "flex",
                            justifyContent: "space-between",
                        },
                    }}
                >
                    <Typography variant={"h5"}>{title}</Typography>
                    <Box
                        sx={{
                            "@media print": {
                                display: "none",
                            },
                        }}
                    >
                        <IconButton
                            aria-label={t(expanded ? "accessibility.collapseSection" : "accessibility.expandSection", { title })}
                            onClick={() => setExpanded(!expanded)}
                        >
                            {icon}
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Collapse in={expanded} orientation={"vertical"}>
                {children}
            </Collapse>
        </Box>
    );
};

// ======================
// CollapsibleSubheader
// ======================

export const CollapsibleSubheader: React.FC<CollapsibleHeaderProps> = (p) => {
    const { title, children, indent } = p;
    const theme = useTheme();

    const [expanded, setExpanded] = useState(true);
    const icon = expanded ? <IoIosArrowUp /> : <IoIosArrowDown />;
    const { t } = useTranslation("reports");

    return (
        <Box ml={indent ?? 0}>
            <Box
                sx={{
                    "@media screen": {
                        padding: 2,
                        marginRight: 2,
                        backgroundColor: theme.palette.cirrusGray.main,
                        border: `1px solid ${theme.palette.cirrusGray.dark}`,
                    },
                }}
            >
                <Box
                    sx={{
                        "@media print": {
                            display: "block",
                        },
                        "@media screen": {
                            display: "flex",
                            justifyContent: "space-between",
                        },
                    }}
                >
                    <Typography variant={"h6"}>{title}</Typography>
                    <Box
                        sx={{
                            "@media print": {
                                display: "none",
                            },
                        }}
                    >
                        <IconButton
                            aria-label={t(expanded ? "accessibility.collapseSection" : "accessibility.expandSection", { title })}
                            onClick={() => setExpanded(!expanded)}
                        >
                            <SvgIcon htmlColor={"black"}>{icon}</SvgIcon>
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Collapse in={expanded} orientation={"vertical"}>
                {children}
            </Collapse>
        </Box>
    );
};

// ======================
// ReportFooter
// ======================

interface ReportFooterProps {
    reportInfo: ReportInfo.AsObject;
}

export const ReportFooter: React.FC<ReportFooterProps> = (p) => {
    const { reportInfo } = p;
    const { t } = useTranslation("reports");
    return (
        <>
            <Box pr={4} pl={4} pt={2}>
                <Divider />
            </Box>
            <Box pr={6} pl={6} pb={6} pt={2}>
                <Typography variant={"caption"}>
                    <Grid container>
                        <Grid size={2}>{t("footer.reportId")}</Grid>
                        <Grid size={10}>{reportInfo.reportId}</Grid>
                        <Grid size={2}>{t("footer.generatedAt")}</Grid>
                        <Grid size={10}>{formatISO(convertTimestampObjectToDate(reportInfo?.createdAt))}</Grid>
                        <Grid size={2}>{t("footer.generatedBy")}</Grid>
                        <Grid size={10}>
                            {reportInfo?.createdBy
                                ? t("footer.generatedByValue", {
                                      name: getUserFullNameFromObject(reportInfo.createdBy),
                                      email: reportInfo.createdBy.email,
                                  })
                                : ""}
                        </Grid>
                        <Grid size={2}>{t("footer.notes")}</Grid>
                        <Grid size={10} sx={{ wordBreak: "break-word" }}>
                            {reportInfo?.systemNotes}
                        </Grid>
                        <Grid size={2}>{t("footer.userNotes")}</Grid>
                        <Grid size={10} sx={{ wordBreak: "break-word" }}>
                            {reportInfo?.userNotes}
                        </Grid>
                    </Grid>
                </Typography>
                <Box pt={2} textAlign={"center"}>
                    <Box pb={1}>
                        <Typography variant={"body2"} fontWeight={600}>
                            {t("footer.endOfReport")}
                        </Typography>
                    </Box>

                    <img src={CdsLogo} alt={"Cirrus Data Solutions Inc."} width={"150"} height={"auto"} />
                </Box>
            </Box>
        </>
    );
};

export const getReportListSortFilterConfig = (): ColumnSortFilterConfig<ListProjectReports.Request, ListProjectReports.Request.Sort, any, any> => {
    return {
        sort: {
            defaultSort: {
                fieldId: ListProjectReports.Request.Sort.Field.CREATED_AT,
                label: appI18n.t("reports:filters.creationTime"),
            },
            sortFields: [
                {
                    fieldId: ListProjectReports.Request.Sort.Field.CREATED_AT,
                    label: appI18n.t("reports:filters.creationTime"),
                },
                {
                    fieldId: ListProjectReports.Request.Sort.Field.VERSION,
                    label: appI18n.t("reports:filters.version"),
                },
                {
                    fieldId: ListProjectReports.Request.Sort.Field.SESSION_ID,
                    label: appI18n.t("reports:filters.sessionId"),
                },
                {
                    fieldId: ListProjectReports.Request.Sort.Field.SYSTEM_ID,
                    label: appI18n.t("reports:filters.systemId"),
                },
            ],
            getSortParam: (s: SortState) => new ListProjectReports.Request.Sort().setField(s.field.fieldId).setDesc(s.descending.value),
            defaultSortDesc: true,
        },
        filter: [
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.CREATED_AT,
                label: appI18n.t("reports:filters.created"),
                filterType: "date",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.DateFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setCreatedAt(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.CREATED_BY,
                label: appI18n.t("reports:filters.createdBy"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setCreatedBy(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.USER_NOTES,
                label: appI18n.t("reports:filters.userNotes"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setUserNotes(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.SYSTEM_NOTES,
                label: appI18n.t("reports:filters.systemNotes"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setSystemNotes(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.SYSTEM_ID,
                label: appI18n.t("reports:filters.systemId"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setSystemId(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.SESSION_ID,
                label: appI18n.t("reports:filters.sessionId"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setSessionId(f));
                },
            },
            {
                fieldId: ListProjectReports.Request.Filter.FieldCase.VERSION,
                label: appI18n.t("reports:filters.version"),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectReports.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectReports.Request.Filter().setVersion(f));
                },
            },
        ],
    };
};
